import { useState, useCallback } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { GroupCard } from '../../features/search/cards/GroupCard'
import { Skeleton } from '../../components/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { VirtualizedList } from '../../components/VirtualizedList'
import { useDebounce } from '../../hooks/useDebounce'
import type { SearchResultItem } from '../../stores/searchStore'

/* ───── API Response Types ───── */

interface SearchHit {
  document: Record<string, unknown>
  score: number
  highlight?: Record<string, string>
  highlights?: Array<{
    field: string
    matched_tokens?: string[]
    snippet?: string
  }>
}

interface SearchResults {
  hits: SearchHit[]
  found: number
  page: number
  per_page: number
}

interface SearchResponse {
  entities: SearchResults | null
  groups: SearchResults | null
  messages: SearchResults | null
  facet_counts?: Record<string, { value: string; count: number }[]>
  query_time_ms: number
}

/* ───── Search Params Schema ───── */

interface GroupsBrowseParams {
  q?: string
  platform?: string
  country?: string
  sort?: string
}

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/groups/')({
  validateSearch: (search: Record<string, unknown>): GroupsBrowseParams => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    platform: typeof search.platform === 'string' ? search.platform : undefined,
    country: typeof search.country === 'string' ? search.country : undefined,
    sort: typeof search.sort === 'string' ? search.sort : undefined,
  }),
  component: GroupsBrowsePage,
})

/* ───── Helpers ───── */

function extractHighlights(hit: SearchHit): Record<string, string> | undefined {
  const result: Record<string, string> = {}
  let hasAny = false
  if (hit.highlights && Array.isArray(hit.highlights)) {
    for (const h of hit.highlights) {
      if (h.snippet) {
        result[h.field] = h.snippet
        hasAny = true
      }
    }
  }
  if (hit.highlight) {
    for (const [field, snippet] of Object.entries(hit.highlight)) {
      if (snippet && !result[field]) {
        result[field] = snippet
        hasAny = true
      }
    }
  }
  return hasAny ? result : undefined
}

function hitsToItems(hits: SearchHit[]): SearchResultItem[] {
  return hits.map((hit) => {
    const doc = hit.document
    return {
      id: String(doc.id ?? ''),
      type: 'group' as const,
      name: String(doc.name ?? ''),
      country: doc.country as string | undefined,
      platform: doc.platform as string | undefined,
      member_count: doc.member_count as number | undefined,
      last_activity: doc.last_activity as string | undefined,
      description: doc.description as string | undefined,
      highlights: extractHighlights(hit),
    }
  })
}

/* ───── Constants ───── */

const PLATFORMS = [
  'telegram', 'whatsapp', 'signal', 'discord', 'facebook',
  'twitter', 'instagram', 'tiktok', 'youtube', 'reddit',
]

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'name', label: 'Name' },
  { value: 'relevance', label: 'Relevance' },
]

/* ───── Page Component ───── */

function GroupsBrowsePage() {
  const searchParams = Route.useSearch()
  const navigate = useNavigate()

  const [searchInput, setSearchInput] = useState(searchParams.q ?? '')
  const debouncedSearch = useDebounce(searchInput, 300)

  const query = debouncedSearch || '*'
  const platform = searchParams.platform ?? ''
  const country = searchParams.country ?? ''
  const sort = searchParams.sort ?? 'newest'

  const numberFormatter = new Intl.NumberFormat()

  // Build API params
  const buildParams = useCallback(
    (page: number): Record<string, string | number | boolean | undefined> => ({
      q: query,
      type: 'group',
      page,
      per_page: 25,
      sort,
      platform: platform || undefined,
      country: country || undefined,
    }),
    [query, sort, platform, country],
  )

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ['groups-browse', query, sort, platform, country],
    queryFn: async ({ pageParam = 1, signal }) => {
      const params = buildParams(pageParam as number)
      return api<SearchResponse>('/v1/search', { params, signal })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFound = lastPage.groups?.found ?? 0
      const loadedCount = allPages.reduce(
        (sum, p) => sum + (p.groups?.hits?.length ?? 0),
        0,
      )
      if (loadedCount >= totalFound) return undefined
      return allPages.length + 1
    },
    staleTime: 30_000,
  })

  const results = data?.pages.flatMap((p) => hitsToItems(p.groups?.hits ?? [])) ?? []
  const total = data?.pages[0]?.groups?.found ?? 0

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleCardClick = (id: string) => {
    navigate({ to: '/groups/$id', params: { id } })
  }

  const updateSearch = (updates: Partial<GroupsBrowseParams>) => {
    navigate({
      to: '/groups',
      search: (prev: GroupsBrowseParams) => ({ ...prev, ...updates }),
      replace: true,
    })
  }

  const renderItem = useCallback(
    (item: SearchResultItem) => (
      <GroupCard item={item} onClick={handleCardClick} />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Active filter chips
  const activeFilters: { key: string; label: string; onRemove: () => void }[] = []
  if (platform) {
    activeFilters.push({
      key: 'platform',
      label: platform.charAt(0).toUpperCase() + platform.slice(1),
      onRemove: () => updateSearch({ platform: undefined }),
    })
  }
  if (country) {
    activeFilters.push({
      key: 'country',
      label: country,
      onRemove: () => updateSearch({ country: undefined }),
    })
  }

  return (
    <div className="flex h-full flex-col" data-testid="groups-browse-page">
      {/* Header */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-4">
        <div className="flex items-center justify-between gap-4 max-w-5xl mx-auto">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Groups</h1>
          <div className="flex items-center gap-3">
            {/* Sort */}
            <select
              value={sort}
              onChange={(e) => updateSearch({ sort: e.target.value })}
              className="
                rounded-md border border-[var(--color-border)]
                bg-[var(--color-input)] px-2.5 py-1.5 text-sm
                text-[var(--color-text-primary)]
                focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
              "
              data-testid="groups-sort-select"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-3">
        <div className="max-w-5xl mx-auto">
          <div
            className="
              flex items-center rounded-lg border
              bg-[var(--color-input)] border-[var(--color-border)]
              transition-colors duration-[var(--transition-fast)]
              focus-within:ring-2 focus-within:ring-[var(--color-ring)]
              focus-within:border-[var(--color-accent)]
            "
          >
            <span className="flex items-center pl-4 text-[var(--color-text-muted)]">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
                if (e.target.value) {
                  updateSearch({ q: e.target.value })
                } else {
                  updateSearch({ q: undefined })
                }
              }}
              placeholder="Search groups..."
              className="
                w-full bg-transparent outline-none
                h-10 px-3 text-sm
                text-[var(--color-text-primary)]
                placeholder:text-[var(--color-text-muted)]
              "
              data-testid="groups-search-input"
            />
            {searchInput.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('')
                  updateSearch({ q: undefined })
                }}
                className="flex items-center pr-4 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                aria-label="Clear search"
                data-testid="groups-search-clear"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters row */}
      <div className="border-b border-[var(--color-border)] px-6 py-2">
        <div className="flex items-center gap-3 max-w-5xl mx-auto flex-wrap">
          {/* Platform filter */}
          <select
            value={platform}
            onChange={(e) => updateSearch({ platform: e.target.value || undefined })}
            className="
              rounded-md border border-[var(--color-border)]
              bg-[var(--color-input)] px-2.5 py-1.5 text-xs
              text-[var(--color-text-secondary)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
            "
            data-testid="groups-platform-filter"
          >
            <option value="">All Platforms</option>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
            ))}
          </select>

          {/* Country filter */}
          <input
            type="text"
            value={country}
            onChange={(e) => updateSearch({ country: e.target.value || undefined })}
            placeholder="Country code..."
            className="
              rounded-md border border-[var(--color-border)]
              bg-[var(--color-input)] px-2.5 py-1.5 text-xs w-28
              text-[var(--color-text-secondary)]
              placeholder:text-[var(--color-text-muted)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
            "
            data-testid="groups-country-filter"
          />

          {/* Active filter chips */}
          {activeFilters.map((chip) => (
            <span
              key={chip.key}
              className="
                inline-flex items-center gap-1 rounded-full border
                border-[var(--color-border)] bg-[var(--color-bg-tertiary)]
                px-2.5 py-0.5 text-xs font-medium
                text-[var(--color-text-secondary)]
              "
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.onRemove}
                className="ml-0.5 inline-flex items-center justify-center rounded-full p-0.5 hover:bg-[var(--color-bg-primary)]/30 transition-colors focus:outline-none"
                aria-label={`Remove ${chip.label} filter`}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-hidden px-6 py-4">
        <div className="max-w-5xl mx-auto h-full">
          {/* Loading skeleton */}
          {isLoading && (
            <div className="space-y-3" data-testid="groups-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <Skeleton className="h-10 w-16 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error state */}
          {isError && !isLoading && (
            <EmptyState
              variant="error"
              description={error?.message ?? 'An error occurred while loading groups.'}
            />
          )}

          {/* Empty state */}
          {!isLoading && !isError && results.length === 0 && (
            <EmptyState
              variant="search"
              title="No groups found"
              description="Try adjusting your search or filters."
            />
          )}

          {/* Results list */}
          {!isLoading && !isError && results.length > 0 && (
            <>
              <div className="mb-3 text-xs text-[var(--color-text-muted)]" data-testid="groups-results-meta">
                Found {numberFormatter.format(total)} group{total !== 1 ? 's' : ''}
              </div>
              <VirtualizedList
                items={results}
                renderItem={renderItem}
                estimateSize={100}
                overscan={5}
                onEndReached={handleEndReached}
                isLoading={isFetchingNextPage}
                emptyMessage="No groups to display"
                getItemKey={(index) => results[index]?.id ?? index}
                className="h-[calc(100vh-18rem)]"
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
